import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
Vue.use(Vuex);

import router from "../router/index";
import fb from "../services/auth";
import auth from "../plugins/firebase";
import { updatePassword } from "firebase/auth";
import {
    postObject,
    fetchSingleObjectByKey,
    getCompanyLocationsByUser,
    getLocationsByUser,
    updateSingleObjectToFirebase,
    createUserDashboard,
    getUserDashboard,
    getUserDashboards,
    updateDashboard,
    deleteDashboard,
    duplicateDashboard,
    getWidgetTemplates,
    initializeWidgetTemplates,
    initializeWorkOrders,
    initializeInspections,
    createDefaultDashboardsForUser,
    initializeDefaultDashboardTemplates,
} from "../services/firebase-helpers";
import influxData from "./modules/influxData";

export default new Vuex.Store({
    state: {
        auth: null,
        isAuth: false,
        token: localStorage.getItem("token") || null,
        user: null,
        company: null,
        locations: null,
        location: null,
        sensors: [],
        sensor: null,
        alerts: [],
        alert: null,
        statusFilter: null,
        selectedDevice: "ximu3-001", // Default to ximu3-001
        nightMode: localStorage.getItem("nightMode") === "true" || false,
        // Dashboard state
        currentDashboard: null,
        currentDashboardId: null,
        userDashboards: [],
        widgetTemplates: [],
        dashboardLoading: false,
        dashboardError: null,
    },
    getters: {
        isAuthenticated: (state) => !!state.token,
        currentUser: (state) => state.user,
        currentCompany: (state) => state.company,
        currentDevice: (state) => state.selectedDevice || "ximu3-001",
        isNightMode: (state) => state.nightMode,
        // Dashboard getters
        currentDashboard: (state) => state.currentDashboard,
        currentDashboardId: (state) => state.currentDashboardId,
        userDashboards: (state) => state.userDashboards,
        widgetTemplates: (state) => state.widgetTemplates,
        dashboardLoading: (state) => state.dashboardLoading,
        dashboardError: (state) => state.dashboardError,
    },
    mutations: {
        setToken(state, payload) {
            state.token = payload;
            if (payload) {
                localStorage.setItem("token", payload);
            } else {
                localStorage.removeItem("token");
            }
        },
        setAuth(state, auth) {
            state.auth = auth;
            state.isAuth = !!auth;
        },
        setUser(state, user) {
            state.user = user;
        },
        setCompany(state, company) {
            state.company = company;
        },
        setLocation(state, location) {
            state.location = location;
        },
        setSensor(state, sensor) {
            state.sensor = sensor;
        },
        setAlert(state, alert) {
            state.alert = alert;
        },
        setLocations(state, locations) {
            state.locations = locations;
        },
        pushLocation(state, location) {
            state.locations.push(location);
        },
        setStatusFilter(state, status) {
            state.statusFilter = status;
        },
        setSelectedDevice(state, device) {
            state.selectedDevice = device || "ximu3-001";
        },
        setNightMode(state, nightMode) {
            state.nightMode = nightMode;
            localStorage.setItem("nightMode", nightMode.toString());
        },
        // Dashboard mutations
        setCurrentDashboard(state, dashboard) {
            state.currentDashboard = dashboard;
            if (dashboard) {
                state.currentDashboardId = dashboard.dashboardId;
            }
        },
        setCurrentDashboardId(state, dashboardId) {
            state.currentDashboardId = dashboardId;
        },
        setUserDashboards(state, dashboards) {
            state.userDashboards = dashboards;
        },
        addUserDashboard(state, dashboard) {
            state.userDashboards.unshift(dashboard); // Add to beginning
        },
        updateUserDashboard(state, updatedDashboard) {
            const index = state.userDashboards.findIndex(
                (d) => d.dashboardId === updatedDashboard.dashboardId
            );
            if (index !== -1) {
                state.userDashboards[index] = updatedDashboard;
            }
            // Update current dashboard if it's the one being updated
            if (
                state.currentDashboard &&
                state.currentDashboard.dashboardId === updatedDashboard.dashboardId
            ) {
                state.currentDashboard = updatedDashboard;
            }
        },
        removeUserDashboard(state, dashboardId) {
            state.userDashboards = state.userDashboards.filter(
                (d) => d.dashboardId !== dashboardId
            );
            // Clear current dashboard if it was deleted
            if (
                state.currentDashboard &&
                state.currentDashboard.dashboardId === dashboardId
            ) {
                state.currentDashboard = null;
                state.currentDashboardId = null;
            }
        },
        setWidgetTemplates(state, templates) {
            state.widgetTemplates = templates;
        },
        setDashboardLoading(state, loading) {
            state.dashboardLoading = loading;
        },
        setDashboardError(state, error) {
            state.dashboardError = error;
        },
        updateDashboardWidget(state, { widgetId, updates }) {
            if (state.currentDashboard && state.currentDashboard.widgets) {
                const widgetIndex = state.currentDashboard.widgets.findIndex(
                    (w) => w.id === widgetId
                );

                if (widgetIndex !== -1) {
                    state.currentDashboard.widgets[widgetIndex] = {
                        ...state.currentDashboard.widgets[widgetIndex],
                        ...updates,
                    };
                }
            }
        },
        addDashboardWidget(state, widget) {
            if (state.currentDashboard) {
                if (!state.currentDashboard.widgets) {
                    state.currentDashboard.widgets = [];
                }
                state.currentDashboard.widgets.push(widget);
            }
        },
        removeDashboardWidget(state, widgetId) {
            if (state.currentDashboard && state.currentDashboard.widgets) {
                state.currentDashboard.widgets = state.currentDashboard.widgets.filter(
                    (w) => w.id !== widgetId
                );
            }
        },
    },
    actions: {
        async updateDevice({ commit }, device) {
            await updateSingleObjectToFirebase(device, "devices", device.key);
            commit("setSensor", device);
            const user = state.auth;
            commit("setAuth", {...user });
        },
        async updateLocation({ commit }, location) {
            await updateSingleObjectToFirebase(location, "location", location.key);
            commit("setLocation", location);
            const user = state.auth;
            const locations = await getLocationsByUser(state.auth.uid);
            user.locations = locations;
            commit("setLocations", locations);
            commit("setAuth", {...user });
        },
        async handleUserLocations({ commit }, userId) {
            try {
                const { fetchObjects } = await
                import ("../services/firebase-helpers");
                const allUsers = await fetchObjects("users");

                const currentUser = allUsers.find((user) => user.uid === userId);

                if (!currentUser) {
                    commit("setLocations", []);
                    return;
                }

                const companyId = currentUser.companyId;

                if (!companyId) {
                    commit("setLocations", []);
                    return;
                }

                const allLocations = await fetchObjects("location");

                if (!allLocations || !Array.isArray(allLocations)) {
                    commit("setLocations", []);
                    return;
                }

                const userLocations = allLocations.filter(
                    (location) => location.companyId == companyId
                );
                commit("setLocations", userLocations);
            } catch (error) {
                console.error("Error in handleUserLocations:", error);
                commit("setLocations", []);
            }
        },
        async fetchUserLocations({ commit }, userId) {
            await getCompanyLocationsByUser(userId).then((res) => {
                console.log(res, "this");
                commit("setLocations", res.locations);
                const userData = {
                    ...auth,
                    locations: res.locations,
                };
                console.log(res, "userData");
            });
        },
        async fetchUserCompany({ commit, state }) {
            try {
                const user = state.auth;
                console.log(user, "user");
                if (user && user.companyId) {
                    const company = await fetchSingleObjectByKey(
                        "company",
                        user.companyId
                    );
                    commit("setCompany", company);
                    return company;
                }
                return null;
            } catch (error) {
                console.error("Error fetching company:", error);
                return null;
            }
        },
        async loginUser({ commit, state, dispatch }, user) {
            fb.loginUser(user.email, user.password)
                .then(async(res) => {
                    const token = res.accessToken;
                    localStorage.setItem("token", token);
                    localStorage.setItem("uid", res.uid);
                    const authUser = fb.getUserData(res.uid);
                    commit("setToken", token);
                    const locations = await getLocationsByUser(res.uid);
                    commit("setAuth", {...authUser, locations });
                    commit("setLocations", locations);
                    await dispatch("fetchUserCompany");
                    router.push({ path: "/locations" });
                    return state.token;
                })
                .catch((e) => console.log(e));
        },
        async loginUserWithGoogle({ commit, state, dispatch }, user) {
            const token = user.accessToken;
            localStorage.setItem("token", token);
            localStorage.setItem("uid", user.uid);
            const authUser = fb.getUserData(user.uid);
            const locations = await getLocationsByUser(user.uid);
            commit("setLocations", locations);
            commit("setToken", token);
            commit("setAuth", {...authUser, locations });
            await dispatch("fetchUserCompany");
            router.push({ path: "/locations" });
            return state.token;
        },
        async registerUser({ dispatch }, user) {
            await fb
                .registerUser(user)
                .then(async(res) => {
                    // Create default dashboards for the new user
                    try {
                        await dispatch("createDefaultDashboardsForNewUser", res.uid);
                    } catch (error) {
                        console.warn(
                            "Failed to create default dashboards for new user:",
                            error
                        );
                    }
                    dispatch("loginUser", user);
                })
                .catch((e) => console.log(e));
        },
        async registerUserWithGoogle({ dispatch }, user) {
            await fb
                .addUser({...user, company: user.companyName })
                .then(async(res) => {
                    // Create default dashboards for the new user
                    try {
                        await dispatch("createDefaultDashboardsForNewUser", res.uid);
                    } catch (error) {
                        console.warn(
                            "Failed to create default dashboards for new user:",
                            error
                        );
                    }
                    dispatch("loginUser", user);
                })
                .catch((e) => console.log(e));
        },
        async logoutUser({ commit }) {
            await fb.logout(auth);
            commit("setToken", null);
            commit("setAuth", null);
            commit("setUser", null);
            commit("setCompany", null);
            commit("setLocations", null);
            commit("setLocation", null);
            localStorage.clear();
            router.push({ path: "/" });
        },
        async verifyAuth({ commit, state, dispatch }) {
            fb.auth.onAuthStateChanged(async(user) => {
                if (user) {
                    try {
                        var token = await user.getIdToken();
                        commit("setToken", token);

                        const authUser = await fb.getUserData(user.uid);

                        const { fetchObjects } = await
                        import (
                            "../services/firebase-helpers"
                        );
                        const allUsers = await fetchObjects("users");
                        const currentUser = allUsers.find((u) => u.uid === user.uid);

                        let locations = [];
                        if (currentUser && currentUser.companyId) {
                            const allLocations = await fetchObjects("location");
                            locations = allLocations.filter(
                                (location) => location.companyId == currentUser.companyId
                            );
                        }

                        const authObject = {...authUser, locations, uid: user.uid };

                        commit("setAuth", authObject);
                        commit("setLocations", locations);

                        await dispatch("fetchUserCompany");

                        localStorage.setItem("token", token);
                        localStorage.setItem("uid", user.uid);

                        return state.auth;
                    } catch (error) {
                        console.error("Error during auth verification:", error);
                    }
                } else {
                    localStorage.clear();
                    router.push({ path: "/" });
                    return;
                }
            });
        },
        async addLocation({ commit, dispatch }, location) {
            await postObject(location, "location").then((data) => {
                commit("setLocation", location);
                router.push({ path: "/location/" + data.key });
            });
            const locations = await getLocationsByUser(auth.uid);
            commit("setLocations", locations);
        },
        async fetchLocationById({ commit }, id) {
            const location = await fetchSingleObjectByKey("location", id);
            commit("setLocation", location);
            return location;
        },
        selectAlert({ commit }, alert) {
            commit("setAlert", alert);
        },
        selectSensor({ commit }, sensor) {
            commit("setSensor", sensor);
        },
        selectLocation({ commit }, location) {
            commit("setLocation", location);
        },
        selectDevice({ commit }, device) {
            commit("setSelectedDevice", device);
        },
        login({ commit }, user) {
            commit("setUser", user);
        },
        updateStatusFilter({ commit }, status) {
            commit("setStatusFilter", status);
        },
        clearStatusFilter({ commit }) {
            commit("setStatusFilter", null);
        },
        toggleNightMode({ commit, state }) {
            commit("setNightMode", !state.nightMode);
        },
        // New auth actions from the module
        async loginWithCredentials({ commit, dispatch }, credentials) {
            try {
                const user = await fb.loginUser(
                    credentials.email,
                    credentials.password
                );
                const token = user.accessToken;
                const authUser = fb.getUserData(user.uid);
                const locations = await getLocationsByUser(user.uid);
                await dispatch("fetchUserCompany");

                commit("setToken", token);
                commit("setUser", authUser);
                commit("setLocations", locations);
                commit("setAuth", {...authUser, locations });
                localStorage.setItem("token", token);
                localStorage.setItem("uid", user.uid);
                router.push({ path: "/locations" });
                return authUser;
            } catch (error) {
                throw new Error(error.message || "Login failed");
            }
        },
        async logout({ commit }) {
            try {
                await fb.logout(auth);
                commit("setToken", null);
                commit("setUser", null);
                commit("setAuth", null);
                localStorage.clear();
                router.push({ path: "/" });
            } catch (error) {
                console.error("Logout error:", error);
            }
        },
        async updateProfile({ commit, state }, profileData) {
            try {
                const uid = localStorage.getItem("uid");
                if (!uid) throw new Error("No user ID found");

                const updatedUser = await updateSingleObjectToFirebase({...profileData, uid },
                    "users",
                    uid
                );

                commit("setUser", updatedUser);
                return updatedUser;
            } catch (error) {
                throw new Error(error.message || "Failed to update profile");
            }
        },
        async changePassword({ commit }, passwordData) {
            try {
                const uid = localStorage.getItem("uid");
                if (!uid) throw new Error("No user ID found");

                // Use Firebase Auth to update password
                await updatePassword(auth.auth, passwordData.newPassword);
            } catch (error) {
                throw new Error(error.message || "Failed to change password");
            }
        },
        // Dashboard actions
        async initializeDashboard({ commit, dispatch }, dashboardId = null) {
            console.log(
                "🏗️ STORE: Starting dashboard initialization...",
                dashboardId ? `for dashboard ${dashboardId}` : "for default dashboard"
            );
            commit("setDashboardLoading", true);
            commit("setDashboardError", null);

            try {
                // Initialize widget templates first
                await dispatch("initializeWidgetTemplates");

                // Ensure we have templates before proceeding
                const templates = this.getters.widgetTemplates;
                if (!templates || templates.length === 0) {
                    console.warn("🏗️ STORE: No templates loaded, using fallback");
                    commit("setWidgetTemplates", getDefaultWidgetTemplates());
                }

                // Initialize work orders and inspections
                await initializeWorkOrders();
                await initializeInspections();

                // Load all user dashboards
                const dashboards = await getUserDashboards();
                commit("setUserDashboards", dashboards);

                // Get or create specific dashboard
                let dashboard;
                if (dashboardId) {
                    dashboard = await getUserDashboard(null, dashboardId);
                } else {
                    dashboard = dashboards[0]; // Get most recent
                }

                commit("setCurrentDashboard", dashboard);

                console.log(
                    "🏗️ STORE: Dashboard initialization completed successfully"
                );
                console.log("🏗️ STORE: Current dashboard:", dashboard);
                console.log("🏗️ STORE: All dashboards:", dashboards.length);
            } catch (error) {
                console.error("🏗️ STORE: Dashboard initialization failed:", error);
                commit(
                    "setDashboardError",
                    error.message || "Failed to initialize dashboard"
                );

                // Ensure we have fallback templates even on error
                if (!this.getters.widgetTemplates ||
                    this.getters.widgetTemplates.length === 0
                ) {
                    commit("setWidgetTemplates", getDefaultWidgetTemplates());
                }
            } finally {
                commit("setDashboardLoading", false);
            }
        },

        async loadUserDashboards({ commit }) {
            try {
                const dashboards = await getUserDashboards();
                commit("setUserDashboards", dashboards);
                return dashboards;
            } catch (error) {
                console.error("Error loading user dashboards:", error);
                commit("setDashboardError", error.message);
                throw error;
            }
        },

        async switchDashboard({ commit, dispatch }, dashboardId) {
            try {
                commit("setDashboardLoading", true);
                const dashboard = await getUserDashboard(null, dashboardId);
                commit("setCurrentDashboard", dashboard);
                return dashboard;
            } catch (error) {
                console.error("Error switching dashboard:", error);
                commit("setDashboardError", error.message);
                throw error;
            } finally {
                commit("setDashboardLoading", false);
            }
        },

        async createDashboard({ commit, dispatch }, { name, description }) {
            try {
                // Get current user ID from localStorage
                const userId = localStorage.getItem("uid") || "anonymous";

                // Get default dashboard structure but override with user input
                const defaultDashboard = getDefaultDashboard();
                const dashboardData = {
                    ...defaultDashboard,
                    name: name || "New Dashboard",
                    description: description || "",
                };

                const newDashboard = await createUserDashboard(userId, dashboardData);
                commit("addUserDashboard", newDashboard);

                // Switch to the new dashboard
                commit("setCurrentDashboard", newDashboard);

                return newDashboard;
            } catch (error) {
                console.error("Error creating dashboard:", error);
                commit("setDashboardError", error.message);
                throw error;
            }
        },

        async updateDashboardInfo({ commit }, { dashboardId, name, description }) {
            try {
                const updates = { name, description };
                await updateDashboard(dashboardId, updates);

                // Update the dashboard in the store
                const updatedDashboard = {
                    ...this.getters.currentDashboard,
                    ...updates,
                    updatedAt: new Date().toISOString(),
                };
                commit("updateUserDashboard", updatedDashboard);

                return updatedDashboard;
            } catch (error) {
                console.error("Error updating dashboard info:", error);
                commit("setDashboardError", error.message);
                throw error;
            }
        },

        async deleteDashboard({ commit, dispatch, state }, dashboardId) {
            try {
                await deleteDashboard(dashboardId);
                commit("removeUserDashboard", dashboardId);

                // If we deleted the current dashboard, switch to another one
                if (state.currentDashboardId === dashboardId) {
                    const remainingDashboards = state.userDashboards;
                    if (remainingDashboards.length > 0) {
                        await dispatch(
                            "switchDashboard",
                            remainingDashboards[0].dashboardId
                        );
                    } else {
                        // Create a new default dashboard if none remain
                        await dispatch("createDashboard", {
                            name: "My Dashboard",
                            description: "Default dashboard",
                        });
                    }
                }

                return true;
            } catch (error) {
                console.error("Error deleting dashboard:", error);
                commit("setDashboardError", error.message);
                throw error;
            }
        },

        async duplicateDashboard({ commit }, { dashboardId, newName }) {
            try {
                const duplicatedDashboard = await duplicateDashboard(
                    dashboardId,
                    newName
                );
                commit("addUserDashboard", duplicatedDashboard);
                return duplicatedDashboard;
            } catch (error) {
                console.error("Error duplicating dashboard:", error);
                commit("setDashboardError", error.message);
                throw error;
            }
        },

        async saveDashboard({ commit, state }) {
            console.log("💾 SAVE: saveDashboard called");
            console.log("💾 SAVE: Current dashboard:", state.currentDashboard);

            if (!state.currentDashboard) {
                console.error("💾 SAVE: No current dashboard to save!");
                return;
            }

            try {
                console.log(
                    "💾 SAVE: Saving dashboard with ID:",
                    state.currentDashboard.dashboardId
                );
                console.log(
                    "💾 SAVE: Dashboard widgets count:",
                    state.currentDashboard.widgets ?
                    state.currentDashboard.widgets.length :
                    0
                );

                await updateDashboard(state.currentDashboard.dashboardId, {
                    name: state.currentDashboard.name,
                    description: state.currentDashboard.description,
                    layout: state.currentDashboard.layout,
                    widgets: state.currentDashboard.widgets,
                });

                console.log("💾 SAVE: Dashboard saved to Firebase successfully");

                // Update the dashboard in the store with new timestamp
                const updatedDashboard = {
                    ...state.currentDashboard,
                    updatedAt: new Date().toISOString(),
                };
                commit("updateUserDashboard", updatedDashboard);
                console.log("💾 SAVE: Dashboard updated in store");
            } catch (error) {
                console.error("💾 SAVE: Error saving dashboard:", error);
                commit("setDashboardError", error.message);
            }
        },

        async addWidget({ commit, dispatch }, { templateId, position }) {
            console.log("🎨 STORE: Adding widget with templateId:", templateId);
            console.log("🎨 STORE: Position:", position);
            console.log(
                "🎨 STORE: Available templates:",
                this.getters.widgetTemplates.length
            );
            console.log(
                "🎨 STORE: Template IDs:",
                this.getters.widgetTemplates.map((t) => t.id)
            );

            const template = this.getters.widgetTemplates.find(
                (t) => t.id === templateId
            );
            if (!template) {
                console.error("🎨 STORE: Template not found for ID:", templateId);
                console.error(
                    "🎨 STORE: Available templates:",
                    this.getters.widgetTemplates
                );
                return;
            }

            console.log("🎨 STORE: Found template:", template);

            const widget = {
                id: `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                type: template.type,
                templateId: template.id,
                position: position || {
                    x: 0,
                    y: 0,
                    width: template.defaultSize.width,
                    height: template.defaultSize.height,
                },
                config: {...template.configSchema },
                title: template.name,
                dataSource: template.dataEndpoint,
            };

            console.log("🎨 STORE: Created widget:", widget);
            console.log(
                "🎨 STORE: Current dashboard before adding:",
                this.getters.currentDashboard
            );

            commit("addDashboardWidget", widget);
            console.log(
                "🎨 STORE: Widget added to dashboard, current widgets:",
                this.getters.currentDashboard && this.getters.currentDashboard.widgets ?
                this.getters.currentDashboard.widgets.length :
                0
            );
            console.log("🎨 STORE: Saving dashboard...");

            await dispatch("saveDashboard");
            console.log("🎨 STORE: Dashboard saved successfully");
        },

        async removeWidget({ commit, dispatch }, widgetId) {
            commit("removeDashboardWidget", widgetId);
            await dispatch("saveDashboard");
        },

        async updateWidget({ commit, dispatch }, { widgetId, updates }) {
            commit("updateDashboardWidget", { widgetId, updates });
            await dispatch("saveDashboard");
        },

        async initializeWidgetTemplates({ commit }) {
            console.log("🎨 STORE: Initializing widget templates...");
            try {
                // Try to fetch templates from Firebase first
                let templates = await getWidgetTemplates();

                if (!templates || templates.length === 0) {
                    console.log("🎨 STORE: No templates in Firebase, using defaults");
                    // Use local default templates if Firebase is empty
                    templates = getDefaultWidgetTemplates();

                    // Try to save defaults to Firebase for future use
                    try {
                        await initializeWidgetTemplates();
                    } catch (saveError) {
                        console.warn(
                            "🎨 STORE: Could not save templates to Firebase:",
                            saveError
                        );
                    }
                }

                commit("setWidgetTemplates", templates);
                console.log("🎨 STORE: Widget templates loaded:", templates.length);
            } catch (error) {
                console.warn(
                    "🎨 STORE: Error loading templates, using fallback:",
                    error
                );
                // Fallback to local templates
                const fallbackTemplates = getDefaultWidgetTemplates();
                commit("setWidgetTemplates", fallbackTemplates);
            }
        },

        async createDefaultDashboardsForNewUser({ commit }, userId) {
            try {
                console.log(
                    "🏗️ STORE: Creating default dashboards for new user:",
                    userId
                );

                // Initialize dashboard templates first
                await initializeDefaultDashboardTemplates();

                // Create default dashboards for the user
                const dashboards = await createDefaultDashboardsForUser(userId);

                console.log(
                    "🏗️ STORE: Successfully created default dashboards for user:",
                    userId
                );
                return dashboards;
            } catch (error) {
                console.error(
                    "🏗️ STORE: Error creating default dashboards for new user:",
                    error
                );
                throw error;
            }
        },
    },
    modules: {
        influxData,
    },
});

// Helper function to create default dashboard
function getDefaultDashboard() {
    return {
        name: "My Dashboard",
        layout: {
            columns: 12,
            gap: 16,
        },
        widgets: [], // Start with no widgets - blank dashboard
    };
}

// Default widget templates function for fallback
function getDefaultWidgetTemplates() {
    return [
        // Metrics - New IoT/Device focused widgets
        {
            id: "metric_alerts_over_time",
            type: "chart",
            name: "Alerts Over Time",
            description: "Time series chart showing alert trends",
            category: "alerts",
            defaultSize: { width: 6, height: 4 },
            icon: "mdi-chart-timeline-variant",
            configSchema: {
                title: { type: "string", default: "Alerts Over Time" },
                chartType: { type: "string", default: "line" },
                timeRange: { type: "string", default: "30d" },
            },
            dataEndpoint: "/api/charts/alerts-timeline",
        },
        {
            id: "metric_alerts_by_location",
            type: "chart",
            name: "Alerts by Location",
            description: "Bar chart showing alerts grouped by location",
            category: "alerts",
            defaultSize: { width: 6, height: 4 },
            icon: "mdi-map-marker-alert",
            configSchema: {
                title: { type: "string", default: "Alerts by Location" },
                chartType: { type: "string", default: "bar" },
            },
            dataEndpoint: "/api/charts/alerts-by-location",
        },
        {
            id: "metric_gyro_accelerometer",
            type: "chart",
            name: "Gyroscope Data",
            description: "Real-time gyroscope data visualization",
            category: "sensors",
            defaultSize: { width: 8, height: 4 },
            icon: "mdi-axis-arrow",
            configSchema: {
                title: { type: "string", default: "Gyroscope Data" },
                chartType: { type: "string", default: "line" },
                sensors: { type: "array", default: ["gyro"] },
            },
            dataEndpoint: "/api/charts/gyro-data",
        },
        {
            id: "metric_accelerometer_data",
            type: "chart",
            name: "Accelerometer Data",
            description: "Real-time accelerometer data visualization",
            category: "sensors",
            defaultSize: { width: 8, height: 4 },
            icon: "mdi-axis-arrow",
            configSchema: {
                title: { type: "string", default: "Accelerometer Data" },
                chartType: { type: "string", default: "line" },
            },
            dataEndpoint: "/api/charts/accelerometer-data",
        },
        {
            id: "metric_battery_levels",
            type: "chart",
            name: "Battery Level by Device",
            description: "Battery status across all devices",
            category: "devices",
            defaultSize: { width: 6, height: 3 },
            icon: "mdi-battery",
            configSchema: {
                title: { type: "string", default: "Device Battery Levels" },
                chartType: { type: "string", default: "bar" },
            },
            dataEndpoint: "/api/charts/battery-levels",
        },
        {
            id: "metric_device_status",
            type: "chart",
            name: "Device Status",
            description: "Donut chart showing device status distribution",
            category: "devices",
            defaultSize: { width: 4, height: 3 },
            icon: "mdi-wifi",
            configSchema: {
                title: { type: "string", default: "Device Status" },
                chartType: { type: "string", default: "doughnut" },
            },
            dataEndpoint: "/api/charts/device-status",
        },
        {
            id: "metric_work_orders",
            type: "chart",
            name: "Work Orders Created Over Time",
            description: "Timeline of work order creation",
            category: "maintenance",
            defaultSize: { width: 6, height: 4 },
            icon: "mdi-clipboard-list",
            configSchema: {
                title: { type: "string", default: "Work Orders Over Time" },
                chartType: { type: "string", default: "line" },
                timeRange: { type: "string", default: "30d" },
            },
            dataEndpoint: "/api/charts/work-orders-timeline",
        },

        // Additional Recommended Widgets
        {
            id: "metric_device_health_score",
            type: "metric",
            name: "Device Health Score",
            description: "Overall health score across all devices",
            category: "devices",
            defaultSize: { width: 3, height: 2 },
            icon: "mdi-heart-pulse",
            configSchema: {
                title: { type: "string", default: "Device Health" },
                color: { type: "string", default: "#4CAF50" },
            },
            dataEndpoint: "/api/metrics/device-health",
        },
        {
            id: "metric_critical_alerts",
            type: "metric",
            name: "Critical Alerts Count",
            description: "Number of critical alerts requiring attention",
            category: "alerts",
            defaultSize: { width: 3, height: 2 },
            icon: "mdi-alert-octagon",
            configSchema: {
                title: { type: "string", default: "Critical Alerts" },
                color: { type: "string", default: "#f44336" },
            },
            dataEndpoint: "/api/metrics/critical-alerts",
        },
        {
            id: "metric_temperature_trends",
            type: "chart",
            name: "Temperature Trends",
            description: "Temperature monitoring across devices",
            category: "sensors",
            defaultSize: { width: 6, height: 3 },
            icon: "mdi-thermometer",
            configSchema: {
                title: { type: "string", default: "Temperature Trends" },
                chartType: { type: "string", default: "line" },
            },
            dataEndpoint: "/api/charts/temperature-trends",
        },
        {
            id: "metric_maintenance_schedule",
            type: "table",
            name: "Upcoming Maintenance",
            description: "Scheduled maintenance and inspections",
            category: "maintenance",
            defaultSize: { width: 8, height: 4 },
            icon: "mdi-calendar-clock",
            configSchema: {
                title: { type: "string", default: "Maintenance Schedule" },
                limit: { type: "number", default: 10 },
            },
            dataEndpoint: "/api/tables/maintenance-schedule",
        },
        {
            id: "metric_torque_deviation",
            type: "chart",
            name: "Torque Deviation Analysis",
            description: "Analysis of torque deviations over time",
            category: "sensors",
            defaultSize: { width: 6, height: 4 },
            icon: "mdi-wrench",
            configSchema: {
                title: { type: "string", default: "Torque Analysis" },
                chartType: { type: "string", default: "scatter" },
            },
            dataEndpoint: "/api/charts/torque-deviation",
        },
        {
            id: "metric_response_time",
            type: "metric",
            name: "Average Response Time",
            description: "Average time to respond to critical alerts",
            category: "maintenance",
            defaultSize: { width: 3, height: 2 },
            icon: "mdi-clock-fast",
            configSchema: {
                title: { type: "string", default: "Response Time" },
                color: { type: "string", default: "#FF9800" },
            },
            dataEndpoint: "/api/metrics/response-time",
        },
        {
            id: "metric_asset_lifecycle",
            type: "table",
            name: "Asset Lifecycle",
            description: "Device age, warranty status, and replacement planning",
            category: "devices",
            defaultSize: { width: 8, height: 4 },
            icon: "mdi-timeline-clock",
            configSchema: {
                title: { type: "string", default: "Asset Lifecycle" },
                limit: { type: "number", default: 10 },
            },
            dataEndpoint: "/api/tables/asset-lifecycle",
        },
    ];
}